import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BeforeAfterSlider } from "@/components/common/BeforeAfterSlider";
import { Loader2, Sparkles, ImageIcon } from "lucide-react";

// Autenticazione via cookie httpOnly (inviato automaticamente sui tag <img> same-site).
const photoUrl = (path) => `${API}/files/${path}`;

export default function PortalTransformations() {
  const [photos, setPhotos] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [beforeId, setBeforeId] = useState("");
  const [afterId, setAfterId] = useState("");
  useEffect(() => {
    api.get("/portal/photos").then((r) => {
      setPhotos(r.data);
      const b = r.data.find((p) => p.kind === "before");
      const a = r.data.find((p) => p.kind === "after");
      if (b) setBeforeId(b.id);
      if (a) setAfterId(a.id);
    }).catch(() => setPhotos([]));
  }, []);

  const befores = (photos || []).filter((p) => p.kind === "before");
  const afters = (photos || []).filter((p) => p.kind === "after");
  const bPhoto = (photos || []).find((p) => p.id === beforeId);
  const aPhoto = (photos || []).find((p) => p.id === afterId);

  return (
    <div className="space-y-5" data-testid="portal-transformations">
      <div>
        <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Sparkles className="h-6 w-6 text-[hsl(var(--hi-gold))]" /> Le mie trasformazioni</h1>
        <p className="text-sm text-muted-foreground mt-1">Il tuo archivio prima/dopo, privato e riservato.</p>
      </div>

      {befores.length > 0 && afters.length > 0 && (
        <Card className="p-4" data-testid="portal-compare">
          <div className="text-sm font-medium mb-3">Confronto Prima / Dopo</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Select value={beforeId} onValueChange={setBeforeId}><SelectTrigger data-testid="compare-before"><SelectValue placeholder="Prima" /></SelectTrigger><SelectContent>{befores.map((p) => <SelectItem key={p.id} value={p.id}>{p.label || fmtDate(p.created_at)}</SelectItem>)}</SelectContent></Select>
            <Select value={afterId} onValueChange={setAfterId}><SelectTrigger data-testid="compare-after"><SelectValue placeholder="Dopo" /></SelectTrigger><SelectContent>{afters.map((p) => <SelectItem key={p.id} value={p.id}>{p.label || fmtDate(p.created_at)}</SelectItem>)}</SelectContent></Select>
          </div>
          {bPhoto && aPhoto && <BeforeAfterSlider before={photoUrl(bPhoto.storage_path)} after={photoUrl(aPhoto.storage_path)} />}
          <p className="text-[11px] text-muted-foreground mt-2 text-center">Trascina la maniglia per confrontare</p>
        </Card>
      )}

      {photos === null ? (
        <div className="h-48 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div>
      ) : photos.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <ImageIcon className="h-8 w-8 opacity-50" />
          Ancora nessuna foto. Il tuo hair stylist aggiungerà qui le tue trasformazioni.
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p) => (
            <Card key={p.id} className="p-0 overflow-hidden group" data-testid={`portal-photo-${p.id}`}>
              <button onClick={() => setViewer(p)} className="block w-full aspect-square bg-muted overflow-hidden">
                <img src={photoUrl(p.storage_path)} alt={p.label || "trasformazione"} loading="lazy" className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
              </button>
              <div className="p-2 flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="text-xs font-medium truncate">{p.label || "Trasformazione"}</div><div className="text-[10px] text-muted-foreground">{fmtDate(p.created_at)}</div></div>
                <Badge variant={p.kind === "before" ? "secondary" : p.kind === "after" ? "default" : "outline"} className="shrink-0">{p.kind === "before" ? "Prima" : p.kind === "after" ? "Dopo" : "Foto"}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-lg">{viewer?.label || "Trasformazione"}</DialogTitle></DialogHeader>
          {viewer && <img src={photoUrl(viewer.storage_path)} alt={viewer.label || "foto"} className="w-full rounded-lg max-h-[70vh] object-contain bg-muted" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
